import { useRouter } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { Facebook, Linkedin, Phone, Mail, MapPin } from "lucide-react";
import { IAR_DISCLAIMER, HTG_FINANCE_DISCLOSURE, SHOW_ZERO_PERCENT, ZERO_PERCENT_RISK_STATEMENT, FINANCE_CALCULATOR_PATH } from "@/content/finance";
import { FINANCING_I4F_HOW_IT_WORKS_PATH } from "@/content/financing-i4f-how-it-works";
import { SOLAR_LEASE_PLAN_PATH } from "@/content/financing-solar-lease-plan";
import { SITE, ACCREDITATION_LOGOS } from "@/lib/site";
import { EmailContactLink } from "@/components/email-contact-link";
import { HometreeLogo } from "@/components/financing/hometree-logo";
import { CHECK_BRANDS } from "@/lib/services";

function getFooterMetaLine(pathname: string): string {
  const pageMeta: Record<string, string> = {
    "/": "Complete Remedial Solutions · Insulation · Heating · Renewables · Wales & South West",
    "/about": "About CRS Energy · Employee-Owned · PAS 2030 · Wales & South West",
    "/services": "Insulation · Damp · Heating · Renewables · PAS 2030 Certified",
    "/contact": "Contact · Free Assessment · Bridgend · Wales & South West",
    "/the-team": "Meet the Team · PAS 2030 · Accredited Specialists",
    "/damp-survey": "Damp Survey · Evidence-Led Diagnosis · HDC-Led · South Wales",
    "/grants": "Green Homes Plan · Approved Installer · Retrofit Installation · Wales & South West",
    "/coverage": "Coverage · Wales & South West · Local Installers",
    "/accreditations": "Accreditations · TrustMark · MCS · Gas Safe · Stroma",
    "/privacy": "Privacy Policy · GDPR · CRS Energy",
    "/data-protection": "Data Protection · GDPR · CRS Energy",
    "/air-source-heat-pumps":
      "Air Source Heat Pumps · MCS Certified · Heat-Loss Designed · Manufacturer-Backed",
    "/solar-pv": "Solar PV · Battery Storage · DNO Compliance · MCS Certified",
    "/commercial-solar-pv": "Commercial Solar PV · Battery Storage · DNO Compliance · MCS Certified",
    "/commercial":
      "Commercial & Public Sector · PAS 2035 Retrofit · Framework-Ready · Social Value Reporting",
    "/commercial-structural":
      "Structural & Remedial · Structural Survey · Tested Tie Systems · Building-Control Liaison",
    "/commercial-retrofit":
      "Whole-House Retrofit · PAS 2035 Coordinated · TrustMark · TEP-Aligned",
    "/commercial-heat-pumps":
      "Heat Pump Retrofit Programmes · MCS Certified · Heat-Loss Designed · Framework-Ready",
    "/commercial-damp-mould-ventilation":
      "Damp, Mould & Ventilation · Inspection-Led · PAS 2030 · Insurance-Backed",
    "/commercial-insulation":
      "Insulation Retrofit Programmes · PAS 2030 · TrustMark Lodgement · Conformity Documented",
    "/commercial-heating":
      "Gas, Boiler & Heating Programmes · LGSR / CP12 · Gas Safe Registered · Insurance-Backed",
    "/commercial-compliance": "Compliance & Accreditations · MCS · TrustMark · Insurance-Backed",
    "/commercial-social-value": "Social Value · Local Jobs · Apprenticeships · Community Benefit",
    "/commercial-case-studies": "Case Studies · Framework Programmes · Evidenced Outcomes",
    "/for-trade": "Partnerships · Framework-Ready · Accredited · Insurance-Backed",
    "/loft-insulation": "Loft Insulation · PAS 2030 · TrustMark · Ventilation Coordinated",
    "/internal-wall-insulation":
      "Internal Wall Insulation · PAS 2030 · TrustMark · Ventilation Coordinated",
    "/heating-controls":
      "Heating Controls · Gas Safe Registered · MCS Accredited · Manufacturer-Backed",
    "/boilers-heating":
      "Boilers & Heating · Gas Safe Registered · Manufacturer-Backed Warranty",
    "/remedial-wall-ties":
      "Remedial Wall Ties · Structural Survey · Tested Tie Systems · Insurance-Backed Guarantee",
    "/ventilation-extraction": "Ventilation & Extraction · PAS 2030 · Whole-House Assessment",
    "/commercial-sectors/landlords":
      "Landlords · Gas Safe · LGSR · Damp & Mould · Framework-Ready",
    "/financing": "Energy Efficiency · Retrofit · Heating · Solar · Remedial",
    "/financing-i4f-how-it-works":
      "Ideal4Finance · Monthly Payment Options · Application Journey · CRS Energy",
  };
  const service = pageMeta[pathname];
  if (service) return `CRS Energy · ${service}`;
  if (pathname.startsWith("/commercial-sectors/")) {
    return "CRS Energy · Retrofit · Heating · Remedial · Framework-Ready";
  }
  return "CRS Energy · Cavity Wall Insulation Removal · Insulation · Heating · Structural";
}


export function SiteFooter() {
  const router = useRouter();

  const metaLine = getFooterMetaLine(router.state.location.pathname);
  return (
    <footer className="bg-primary text-primary-foreground mt-20">
      <div className="container-page py-14 grid gap-10 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <div>
          <h3 className="text-lg font-semibold mb-4">Complete Remedial Solutions (CRS) Ltd</h3>
          <p className="text-sm text-primary-foreground/80 mb-3">
            Trading as <span className="font-semibold text-primary-foreground">CRS Energy</span>
          </p>
          <address className="not-italic text-sm space-y-1 text-primary-foreground/80">
            <div className="flex gap-2">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
              <span>
                {SITE.address.line1}
                <br />
                {SITE.address.line2}
                <br />
                {SITE.address.city}, {SITE.address.postcode}
              </span>
            </div>
          </address>
          <div className="text-xs text-primary-foreground/60 mt-4 whitespace-pre-line">
            Registered in England & Wales{"\n"}
            Company No. {SITE.companyNumber}{"\n"}
            {"\n"}
            MCS No.&nbsp;IAA-10295{"\n"}
            Trustmark No.&nbsp;2925406{"\n"}
            HIES Membership No.&nbsp;CRS/A/1864
          </div>
          <div className="flex gap-3 mt-5">
            <a
              href={SITE.social.facebook}
              aria-label="Facebook"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-full bg-primary-foreground/10 hover:bg-accent transition-colors min-h-11 min-w-11 inline-flex items-center justify-center"
            >
              <Facebook className="h-4 w-4" />
            </a>
            <a
              href={SITE.social.linkedin}
              aria-label="LinkedIn"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-full bg-primary-foreground/10 hover:bg-accent transition-colors min-h-11 min-w-11 inline-flex items-center justify-center"
            >
              <Linkedin className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Contact</h3>
          <ul className="space-y-2 text-sm text-primary-foreground/80">
            <li>
              <a href={SITE.phoneTel} className="inline-flex items-center gap-2 hover:text-accent-soft">
                <Phone className="h-4 w-4" aria-hidden /> {SITE.phone}
              </a>
            </li>
            <li>
              <a href={SITE.phoneSecondaryTel} className="inline-flex items-center gap-2 hover:text-accent-soft">
                <Phone className="h-4 w-4" aria-hidden /> {SITE.phoneSecondary}
              </a>
            </li>
            <li>
              <EmailContactLink className="inline-flex items-center gap-2 hover:text-accent-soft">
                <span className="inline-flex items-center gap-2">
                  <Mail className="h-4 w-4" aria-hidden /> {SITE.email}
                </span>
              </EmailContactLink>
            </li>
          </ul>
          <h4 className="text-sm font-semibold uppercase tracking-wider mt-6 mb-3 text-primary-foreground/70">
            Legal
          </h4>
          <ul className="space-y-1.5 text-sm text-primary-foreground/80">
            <li><Link to="/" className="hover:text-accent-soft">Home</Link></li>
            <li><Link to="/privacy" className="hover:text-accent-soft">Privacy Policy</Link></li>
            <li><Link to="/data-protection" className="hover:text-accent-soft">Data Protection</Link></li>
            <li><Link to="/complaints-policy" className="hover:text-accent-soft">Complaints Policy</Link></li>
            <li><Link to="/consumer-duty-policy" className="hover:text-accent-soft">Consumer Duty Policy</Link></li>
            <li><Link to="/contact" className="hover:text-accent-soft">Contact</Link></li>
            <li><Link to="/grants" className="hover:text-accent-soft">Grants</Link></li>
            <li><Link to="/coverage" className="hover:text-accent-soft">Coverage</Link></li>
            <li><Link to="/accreditations" className="hover:text-accent-soft">Accreditations</Link></li>
            <li><Link to="/solar-grants-wales" className="hover:text-accent-soft">Solar Grants Wales</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">The Home Check Group</h3>
          <p className="text-sm text-primary-foreground/70 mb-4">
            Four specialist diagnostic brands. One trusted contractor.
          </p>
          <ul className="space-y-2 text-sm text-primary-foreground/80">
            <li>
              <Link to="/the-home-check-group" className="hover:text-accent-soft font-medium">
                About the group →
              </Link>
            </li>
            {(Object.keys(CHECK_BRANDS) as Array<keyof typeof CHECK_BRANDS>).map((key) => (
              <li key={key}>
                <a
                  href={CHECK_BRANDS[key].url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-accent-soft"
                >
                  {CHECK_BRANDS[key].name} →
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Finance</h3>
          <ul className="space-y-1.5 text-sm text-primary-foreground/80 mb-4">
            <li><Link to="/financing" className="hover:text-accent-soft">Payment Options</Link></li>
            <li><Link to={FINANCING_I4F_HOW_IT_WORKS_PATH} className="hover:text-accent-soft">Loan Finance - Ideal4Finance</Link></li>
            <li><Link to={SOLAR_LEASE_PLAN_PATH} className="hover:text-accent-soft">Solar Lease Plan - Hometree Finance</Link></li>
            <li><Link to={FINANCE_CALCULATOR_PATH} className="hover:text-accent-soft">Calculators</Link></li>
          </ul>
          <div className="text-[11px] text-primary-foreground/75 leading-relaxed space-y-2 mt-4">
            <HometreeLogo variant="white" className="h-5 mb-1" />
            <p>{IAR_DISCLAIMER}</p>
            <p>{HTG_FINANCE_DISCLOSURE}</p>
            {SHOW_ZERO_PERCENT && <p>{ZERO_PERCENT_RISK_STATEMENT}</p>}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Partnerships</h3>
          <ul className="space-y-2 text-sm text-primary-foreground/80">
            <li><a href="/for-trade" className="hover:text-accent-soft font-medium">Partnerships Hub →</a></li>
            <li><a href="/commercial" className="hover:text-accent-soft">Commercial Overview</a></li>
            <li><a href="/commercial-sectors/councils" className="hover:text-accent-soft">Councils</a></li>
            <li><a href="/commercial-sectors/housing-associations" className="hover:text-accent-soft">Housing Associations</a></li>
            <li><a href="/commercial-sectors/main-contractors" className="hover:text-accent-soft">Main Contractors</a></li>
            <li><a href="/commercial-sectors/facilities-management" className="hover:text-accent-soft">Facilities Management</a></li>
            <li><a href="/commercial-sectors/landlords" className="hover:text-accent-soft">Landlords</a></li>
            <li><a href="/commercial-sectors/insurers" className="hover:text-accent-soft">Insurers</a></li>
            <li><a href="/commercial-compliance" className="hover:text-accent-soft">Compliance & Accreditations</a></li>
            <li><a href="/commercial-case-studies" className="hover:text-accent-soft">Case Studies</a></li>
          </ul>
        </div>



        <div>
          <h3 className="text-lg font-semibold mb-4">Why Choose CRS</h3>
          <ul className="space-y-2 text-sm text-primary-foreground/80">
            <li>Lines open 24/7</li>
            <li>Local experts in Wales and the South West</li>
            <li>Outstanding customer service record</li>
            <li>Fully accredited and insured</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-primary-foreground/10">
        <div className="container-page py-6">
          <div className="flex items-center gap-2 justify-center mb-3">
            <span className="h-px w-8 bg-primary-foreground/25" />
            <p className="text-[10px] font-semibold uppercase tracking-widest text-primary-foreground/70">
              Accredited & Certified
            </p>
            <span className="h-px w-8 bg-primary-foreground/25" />
          </div>
          <div
            className="relative overflow-hidden"
            style={{
              maskImage: "linear-gradient(to right, transparent 0, black 5%, black 95%, transparent 100%)",
              WebkitMaskImage: "linear-gradient(to right, transparent 0, black 5%, black 95%, transparent 100%)",
            }}
            aria-label="Accreditations"
          >
            <div className="flex gap-10 md:gap-14 animate-[accreditations-scroll_32s_linear_infinite] w-max items-center">
              {[0, 1].map((loop) => (
                <div key={loop} className="flex items-center gap-10 md:gap-14 shrink-0">
                  {ACCREDITATION_LOGOS.map((logo, i) => (
                    <div key={`${loop}-${i}`} className="flex items-center justify-center h-12 rounded-md bg-white/95 px-3">
                      <img
                        src={logo.src}
                        alt={logo.alt}
                        className="h-9 md:h-10 w-auto max-w-[8rem] object-contain"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-primary-foreground/10">
        <div className="container-page py-5 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-primary-foreground/60">
          <p>
            © {new Date().getFullYear()} Complete Remedial Solutions (CRS) Ltd, trading as CRS Energy.{" "}
            <a href={SITE.emailHref} className="hover:text-accent-soft transition-colors">
              {SITE.email}
            </a>
            . Registered in England &amp; Wales. All rights reserved.
          </p>
          <p>{metaLine}</p>
        </div>
      </div>
    </footer>
  );
}
